import { useParams, useNavigate } from "react-router-dom"
import { useUniversities } from "@src/presentation/hooks/universities"
import {
    Button,
    Card,
    CardBody,
    CardHeader,
    Chip,
    Spinner,
    Divider
} from "@heroui/react"
import {
    ChevronLeft,
    Building2,
    Edit,
    MapPin,
    Globe,
    XCircle,
    CheckCircle
} from "lucide-react"

export default function UniversityDetailPage() {
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()

    // Fetch universities and find the specific one
    const { data: universitiesData, isLoading, error } = useUniversities()

    const university = universitiesData?.universities?.find(u => u.university_id === id)

    // Since we check for university existence below, we can assert it's not null here
    const safeUniversity = university!

    if (isLoading) {
        return (
            <div className="flex items-center justify-center p-8">
                <Spinner size="lg" />
            </div>
        )
    }

    if (error || !university) {
        return (
            <div className="flex flex-col items-center justify-center p-8 text-danger">
                <XCircle className="h-12 w-12 mb-2" />
                <p>{error ? `Error: ${error.message}` : 'University not found'}</p>
            </div>
        )
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-background via-background to-default-50">
            {/* Header */}
            <header className="border-b border-border bg-background/80 backdrop-blur-xl supports-[backdrop-filter]:bg-background/60 sticky top-0 z-20">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col sm:flex-row min-h-[56px] sm:h-16 py-2 sm:py-0 items-start sm:items-center justify-between gap-2 sm:gap-0">
                        <div className="flex items-center gap-3">
                            <Button
                                variant="light"
                                size="sm"
                                onPress={() => navigate(-1)}
                                startContent={<ChevronLeft className="h-4 w-4" />}
                                className="hover:scale-105 transition-transform flex-shrink-0"
                            >
                                <span className="hidden sm:inline">Back</span>
                                <span className="sm:hidden">Back</span>
                            </Button>
                            <Divider orientation="vertical" className="h-6 hidden sm:block" />
                            <div className="flex items-center gap-3">
                                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-primary to-primary-600 shadow-md">
                                    <Building2 className="h-4 w-4 text-primary-foreground" />
                                </div>
                                <div>
                                    <h1 className="text-xl font-semibold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">{safeUniversity.name}</h1>
                                    <p className="text-xs text-default-500 font-medium">
                                        University Details
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
                            <Chip
                                color={safeUniversity.is_active ? 'success' : 'danger'}
                                variant="flat"
                                startContent={safeUniversity.is_active ? <CheckCircle className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
                                className="flex-shrink-0"
                            >
                                {safeUniversity.is_active ? 'Active' : 'Inactive'}
                            </Chip>
                            <Button
                                color="primary"
                                startContent={<Edit className="h-4 w-4" />}
                                onPress={() => navigate(`/universities/${safeUniversity.university_id}/edit`)}
                                className="hover:scale-105 transition-transform font-semibold rounded w-full sm:w-auto"
                                size="sm"
                            >
                                <span className="hidden sm:inline">Edit University</span>
                                <span className="sm:hidden">Edit</span>
                            </Button>
                        </div>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="max-w-4xl mx-auto space-y-6">

                    {/* University Information */}
                    <div className="grid gap-6 md:grid-cols-2">
                        {/* Basic Information */}
                        <Card className="border border-border rounded shadow-none bg-secondary">
                            <CardHeader className="border-b border-border">
                                <div className="flex items-center gap-3">
                                    <Building2 className="h-5 w-5 text-primary" />
                                    <h2 className="text-lg font-semibold">Basic Information</h2>
                                </div>
                            </CardHeader>
                            <CardBody className="space-y-4">
                                <div>
                                    <p className="text-sm text-default-500">Name</p>
                                    <p className="font-medium text-foreground">{safeUniversity.name}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-default-500">Code</p>
                                    <code className="text-sm bg-default-100 px-2 py-1 rounded">
                                        {safeUniversity.code}
                                    </code>
                                </div>
                                <div>
                                    <p className="text-sm text-default-500">Status</p>
                                    <Chip
                                        size="sm"
                                        color={safeUniversity.is_active ? 'success' : 'danger'}
                                        variant="flat"
                                    >
                                        {safeUniversity.is_active ? 'Active' : 'Inactive'}
                                    </Chip>
                                </div>
                                <div>
                                    <p className="text-sm text-default-500">Created</p>
                                    <p className="text-sm text-foreground">
                                        {new Date(safeUniversity.created_at).toLocaleDateString()}
                                    </p>
                                </div>
                            </CardBody>
                        </Card>

                        {/* Additional Information */}
                        <Card className="border border-border rounded shadow-none bg-secondary">
                            <CardHeader className="border-b border-border">
                                <h2 className="text-lg font-semibold">Additional Information</h2>
                            </CardHeader>
                            <CardBody className="space-y-4">
                                {safeUniversity.description && (
                                    <div>
                                        <p className="text-sm text-default-500">Description</p>
                                        <p className="text-sm text-foreground">{safeUniversity.description}</p>
                                    </div>
                                )}
                                {safeUniversity.address && (
                                    <div>
                                        <div className="flex items-center gap-2 mb-1">
                                            <MapPin className="h-4 w-4 text-default-400" />
                                            <p className="text-sm text-default-500">Address</p>
                                        </div>
                                        <p className="text-sm text-foreground">{safeUniversity.address}</p>
                                    </div>
                                )}
                                {safeUniversity.website && (
                                    <div>
                                        <div className="flex items-center gap-2 mb-1">
                                            <Globe className="h-4 w-4 text-default-400" />
                                            <p className="text-sm text-default-500">Website</p>
                                        </div>
                                        <a
                                            href={safeUniversity.website}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-sm text-primary hover:underline"
                                        >
                                            {safeUniversity.website}
                                        </a>
                                    </div>
                                )}
                                {!safeUniversity.description && !safeUniversity.address && !safeUniversity.website && (
                                    <p className="text-sm text-default-500 italic">No additional information available</p>
                                )}
                            </CardBody>
                        </Card>
                    </div>
                </div>
            </main>
        </div>
    )
}